import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { IoIosAdd } from 'react-icons/io';
import { SEARCH_GET } from '../../actions/types';
import { getSearch, getSearchList } from '../../actions/search';
import { resetErrorCase } from '../../actions/case';
import ModalStandalone from '../../lib/ModalStandalone';
import CaseFilters from '../../components/CaseFilters';
import CasesList from '../../components/list/CasesList';
import CaseLegend from '../../components/CaseLegend';
import ModalSendEmail from '../../components/ModalSendEmail';
import Loader from '../../components/Loader';
import CaseForm from '../../components/form/CaseForm';
import noCaseImg from '../../assets/images/no-case.png';
import styles from './cases.module.scss';
import { canCreateCase } from '../../utils';

const Cases = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpened, setIsOpened] = useState(null);
  const [sendDocument, setSendDocument] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  const { searchReducer, authReducer } = useSelector((store) => store);
  const { selectedAgency, user } = authReducer;
  const {
    cases,
    list,
    isLoading,
    casesCount,
  } = searchReducer;
  const modalCreate = useRef();

  useEffect(() => {
    getSearchList(dispatch);
  }, [selectedAgency]);

  useEffect(() => {
    if (!Object.values(list).length) return;
    const params = new URLSearchParams(searchParams);
    params.set('page', currentPage);
    params.set('number', itemsPerPage);
    getSearch(dispatch, params, list);
  }, [searchParams, list, selectedAgency, currentPage, itemsPerPage]);

  useEffect(() => {
    if (typeof isOpened === 'boolean') return;
    const params = new URLSearchParams(searchParams);
    const search = Object.fromEntries(params);
    const {
      users,
      status,
      clients,
      creationDate,
    } = search;
    const isSearchParams = Object.values({
      users,
      status,
      clients,
      creationDate,
    }).filter((d) => d).length > 0;
    setIsOpened(isSearchParams);
  }, [searchParams, isOpened]);

  useEffect(() => {
    const params = Object.fromEntries(searchParams);
    if (params.createCase !== undefined) {
      console.log('open modal');
      modalCreate.current.open();
    } else {
      modalCreate.current.close();
    }
  }, [searchParams]);

  function handleClose() {
    const params = new URLSearchParams(searchParams);
    params.delete('createCase');
    setSearchParams(params);
    resetErrorCase(dispatch);
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const sort = {
    key: searchParams.get('sortKey') || 'ref',
    order: searchParams.get('sortOrder') || 'asc',
  };

  const handleSortChange = ({ key, order }) => {
    setCurrentPage(1);
    const params = new URLSearchParams(searchParams);
    params.set('sortKey', key);
    params.set('sortOrder', order);
    setSearchParams(params);
  };

  return (
    <div className={styles.cases}>
      <ModalSendEmail sendDocument={sendDocument} />
      <ModalStandalone ref={modalCreate} handleClose={handleClose}>
        <CaseForm handleClose={handleClose} />
      </ModalStandalone>
      <div className={styles.search}>
        <CaseFilters isOpened={isOpened} setIsOpened={setIsOpened} />
      </div>
      <div className={styles.container}>
        {(isLoading.includes(SEARCH_GET))
          && <div className={styles.loader}><Loader /></div>
        }
        {cases !== null && (
          <div>
            {cases?.length > 0
              ? <>
                <div className={styles.containerCases}>
                  <div className={styles.header}>
                    <h3>Affaires</h3>
                    <CaseLegend />
                  </div>
                  <div>
                    <CasesList
                      cases={cases}
                      sort={sort}
                      onSortChange={handleSortChange}
                      sendCase={(doc) => {
                        setSendDocument(doc);
                      }}
                    />
                  </div>
                </div>
                <div className={styles.pagination}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="secondary"
                  >
                    Précédent
                  </button>
                  <div className={styles.pageNumbers}>
                    {(() => {
                      const totalPages = Math.ceil(casesCount / itemsPerPage);
                      const pages = [];
                      // Première page
                      if (totalPages > 0) {
                        pages.push(1);
                      }
                      // Points de suspension après la première page
                      if (currentPage > 3) {
                        pages.push('...');
                      }
                      // Une page avant la page courante
                      const start = Math.max(2, currentPage - 1);
                      const end = Math.min(totalPages - 1, currentPage + 1);
                      for (let i = start; i <= end; i++) {
                        pages.push(i);
                      }
                      // Points de suspension avant la dernière page
                      if (currentPage < totalPages - 2) {
                        pages.push('...');
                      }
                      // Dernière page
                      if (totalPages > 1) {
                        pages.push(totalPages);
                      }
                      return pages.map((page, index) => {
                        if (page === '...') {
                          return (
                            <span
                              key={`ellipsis-${index}`}
                              className={styles.ellipsis}
                            >
                              ...
                            </span>
                          );
                        }
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`${styles.pageButton} ${
                              currentPage === page ? styles.active : ''
                            }`}
                          >
                            {page}
                          </button>
                        );
                      });
                    })()}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= Math.ceil(casesCount / itemsPerPage)}
                    className="secondary"
                  >
                    Suivant
                  </button>
                </div>
              </>
              : <div className={styles.noCase}>
                <p>il n'y a pas encore d'affaires...</p>
                {canCreateCase(user.role) && (
                  <button className='secondary' onClick={() => navigate('/cases?createCase=1')}>
                    <span className='icon'><IoIosAdd size={'20px'} /></span>
                    <span>Créer une affaire</span>
                  </button>
                )}
                <img src={noCaseImg} alt="il n'y a pas encore d'affaires" />
              </div>
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default Cases;
